import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { Activation, LicenseDetail } from "../lib/types";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Select,
  StatusBadge,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
  formatRelative,
} from "../components/ui";

const SETTABLE = ["trialing", "active", "past_due", "suspended", "expired"] as const;

/**
 * Recomputed here rather than read from `lastHealthState`, so a deployment that
 * came back between nightly sweeps reads as up straight away. The stored field
 * is the sweep's edge-detector, not the display value.
 */
const MISSED_BEATS_BEFORE_DOWN = 3;

function isReporting(lastSeenAt: string, heartbeatSeconds: number, now: number): boolean {
  const silentMs = now - new Date(lastSeenAt).getTime();
  if (silentMs < 0) return true;
  return silentMs <= heartbeatSeconds * MISSED_BEATS_BEFORE_DOWN * 1000;
}

/**
 * A clock that ticks, so this screen ages by itself.
 *
 * "Last seen" and the reporting badge are both read off the current time, so a
 * component that never re-renders keeps insisting a deployment is healthy long
 * after it went quiet — the one moment somebody is actually staring at the
 * screen waiting to find out. Fifteen seconds is finer than the minute the
 * label is rounded to, so it never shows a stale figure.
 */
function useNow(everyMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(timer);
  }, [everyMs]);
  return now;
}

/** Raw event names are for the database. Staff read this. */
const EVENT_LABELS: Record<string, string> = {
  went_down: "Stopped reporting",
  recovered: "Started reporting again",
  version_changed: "Version changed",
  maintenance_on: "Maintenance turned on",
  maintenance_off: "Maintenance turned off",
};

export function LicenseDetailRoute() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Deleting a licence is owner-only, the same tier that governs staff
  // accounts — see the API. An owner reports as `admin` and carries this flag
  // beside it, so the two checks are separate on purpose.
  const isOwner = user?.isOwner === true;
  const queryClient = useQueryClient();

  const [revoking, setRevoking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [domainDraft, setDomainDraft] = useState<string | null>(null);
  // Declared here rather than after the loading/error guard below, so the
  // component calls the same hooks on every render regardless of query state.
  // Seeded from the fetched licence once it loads (and re-seeded whenever the
  // stored message actually changes, e.g. after maintenance is turned off).
  const [message, setMessage] = useState("");

  const now = useNow();

  const detail = useQuery({
    queryKey: ["license", id],
    queryFn: () => api<LicenseDetail>(`/api/v1/admin/licenses/${id}`),
    // The ticking clock keeps the label honest between fetches; this is what
    // brings a *new* heartbeat in. React Query pauses polling while the tab is
    // in the background, so an open screen nobody is watching costs nothing.
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (detail.data) setMessage(detail.data.license.maintenanceMessage ?? "");
  }, [detail.data?.license.maintenanceMessage]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["license", id] });
    void queryClient.invalidateQueries({ queryKey: ["licenses"] });
  };

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api(`/api/v1/admin/licenses/${id}`, { method: "PATCH", body: { status } }),
    onSuccess: invalidate,
  });

  const setMaintenance = useMutation({
    mutationFn: (body: { maintenanceMode: boolean; maintenanceMessage: string | null }) =>
      api(`/api/v1/admin/licenses/${id}`, { method: "PATCH", body }),
    onSuccess: invalidate,
  });

  // On success there is no licence left to look at, so this leaves for the
  // list rather than refetching a 404.
  const remove = useMutation({
    mutationFn: (confirm: string) =>
      api(`/api/v1/admin/licenses/${id}`, { method: "DELETE", body: { confirm } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["licenses"] });
      navigate("/licenses");
    },
  });

  const revoke = useMutation({
    mutationFn: (confirm: string) =>
      api(`/api/v1/admin/licenses/${id}/revoke`, { method: "POST", body: { confirm } }),
    onSuccess: () => {
      setRevoking(false);
      invalidate();
    },
  });

  const saveDomains = useMutation({
    mutationFn: (hostnames: string[]) =>
      api(`/api/v1/admin/licenses/${id}/domains`, { method: "PUT", body: { hostnames } }),
    onSuccess: () => {
      setDomainDraft(null);
      invalidate();
    },
  });

  const rotateSecret = useMutation({
    mutationFn: (name: string) =>
      api(`/api/v1/admin/licenses/${id}/secrets/${name}/rotate`, { method: "POST", body: {} }),
    onSuccess: invalidate,
  });

  const setActivationRevoked = useMutation({
    mutationFn: (input: { activationId: string; revoked: boolean }) =>
      api(`/api/v1/admin/activations/${input.activationId}/revoked`, {
        method: "POST",
        body: { revoked: input.revoked },
      }),
    onSuccess: invalidate,
  });

  if (detail.isPending) return <p className="text-sm text-dim">Loading…</p>;
  if (detail.isError) {
    const notFound = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <div className="space-y-4">
        <ErrorNote>{notFound ? "No such licence." : "Could not load this licence."}</ErrorNote>
        <Link to="/licenses" className="text-sm text-accent underline underline-offset-4">
          Back to licences
        </Link>
      </div>
    );
  }

  const { license, effective, auditLog, deploymentEvents } = detail.data;
  const confirmPhrase = `${license.product.code}-${license.keyLast4}`;
  const activeSeats = license.activations.filter((row) => row.revokedAt === null).length;
  const heartbeatSeconds =
    typeof (effective.policy as { heartbeatSeconds?: unknown })?.heartbeatSeconds === "number"
      ? (effective.policy as { heartbeatSeconds: number }).heartbeatSeconds
      : 1800;

  return (
    <div className="space-y-6">
      <nav>
        <Link to="/licenses" className="text-sm text-dim underline-offset-4 hover:underline">
          ← Licences
        </Link>
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="tabular text-xl text-ink">
            SF-{license.product.code}-••••••••-{license.keyLast4}
          </h1>
          <p className="text-sm text-dim">
            {license.customer.name} · {license.product.name} · {license.plan.name}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={license.status} />
          {isOwner && (
            <Button variant="danger" onClick={() => setDeleting(true)}>
              Delete
            </Button>
          )}
          {isAdmin && license.status !== "revoked" && (
            <>
              <Select
                aria-label="Set status"
                value={license.status}
                className="w-40"
                onChange={(event) => setStatus.mutate(event.target.value)}
              >
                {SETTABLE.map((value) => (
                  <option key={value} value={value}>
                    {value.replace("_", " ")}
                  </option>
                ))}
              </Select>
              <Button variant="danger" onClick={() => setRevoking(true)}>
                Revoke
              </Button>
            </>
          )}
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Overview" className="lg:col-span-1">
          <dl className="divide-y divide-hairline text-sm">
            <Row label="Seats in use">
              <span className="tabular">
                {activeSeats} / {effective.maxActivations}
              </span>
            </Row>
            <Row label="Expires">{formatDate(license.expiresAt)}</Row>
            <Row label="Issued">{formatDate(license.createdAt)}</Row>
            <Row label="Heartbeat">
              <span className="tabular">{effective.policy.heartbeatSeconds}s</span>
            </Row>
            <Row label="Token lifetime">
              <span className="tabular">{effective.policy.tokenLifetimeSeconds}s</span>
            </Row>
          </dl>
        </Card>

        <Card title="Effective features" className="lg:col-span-2">
          <div className="flex flex-wrap gap-2 p-4">
            {Object.entries(effective.features).length === 0 ? (
              <p className="text-sm text-dim">No features set on the plan or this licence.</p>
            ) : (
              Object.entries(effective.features).map(([name, value]) => (
                <span
                  key={name}
                  className="rounded border border-hairline-strong px-2 py-1 tabular text-muted"
                >
                  {name}: <span className="text-ink">{JSON.stringify(value)}</span>
                </span>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Stages are the whole grace model, so they are shown as the schedule a
          client will actually follow rather than as raw JSON. */}
      <Card title="Degradation schedule">
        <TableWrap>
          <Table>
            <thead className="border-b border-hairline">
              <tr>
                <Th>After</Th>
                <Th>Mode</Th>
              </tr>
            </thead>
            <tbody>
              {effective.policy.stages.map((stage) => (
                <tr key={stage.afterSeconds} className="border-b border-hairline last:border-0">
                  <Td label="After" className="tabular text-muted">
                    {describeDuration(stage.afterSeconds)}
                  </Td>
                  <Td label="Mode">{stage.mode.replace("_", " ")}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Card>

      <Card
        title="Allowed hostnames"
        action={
          isAdmin && domainDraft === null ? (
            <Button
              onClick={() => setDomainDraft(license.domains.map((d) => d.hostname).join("\n"))}
            >
              Edit
            </Button>
          ) : undefined
        }
      >
        {domainDraft === null ? (
          license.domains.length === 0 ? (
            <EmptyState>No hostnames yet, so this licence cannot activate anywhere.</EmptyState>
          ) : (
            <ul className="divide-y divide-hairline">
              {license.domains.map((domain) => (
                <li key={domain.hostname} className="px-4 py-2.5 tabular">
                  {domain.hostname}
                </li>
              ))}
            </ul>
          )
        ) : (
          <form
            className="space-y-3 p-4"
            onSubmit={(event) => {
              event.preventDefault();
              saveDomains.mutate(
                domainDraft
                  .split(/[\s,]+/)
                  .map((value) => value.trim())
                  .filter((value) => value.length > 0),
              );
            }}
          >
            <Field label="Hostnames" hint="One per line. Replaces the whole list.">
              <textarea
                value={domainDraft}
                rows={4}
                className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 tabular text-ink focus:border-accent"
                onChange={(event) => setDomainDraft(event.target.value)}
              />
            </Field>
            {saveDomains.isError && <ErrorNote>{(saveDomains.error as Error).message}</ErrorNote>}
            <div className="flex gap-2">
              <Button type="submit" variant="primary" disabled={saveDomains.isPending}>
                Save hostnames
              </Button>
              <Button type="button" variant="ghost" onClick={() => setDomainDraft(null)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Card>

      {isAdmin && (
        <Card title="Maintenance">
          <div className="space-y-3 p-4">
            <p className="text-sm text-dim">
              Turns the deployment off deliberately. Separate from billing status — the
              customer sees the message below.
            </p>
            <Field label="Maintenance message">
              <Input value={message} onChange={(event) => setMessage(event.target.value)} />
            </Field>
            {license.maintenanceMode ? (
              <Button
                disabled={setMaintenance.isPending}
                onClick={() =>
                  setMaintenance.mutate({ maintenanceMode: false, maintenanceMessage: null })
                }
              >
                Turn off maintenance
              </Button>
            ) : (
              <Button
                variant="danger"
                disabled={setMaintenance.isPending}
                onClick={() =>
                  setMaintenance.mutate({
                    maintenanceMode: true,
                    maintenanceMessage: message.trim() === "" ? null : message.trim(),
                  })
                }
              >
                Turn on maintenance
              </Button>
            )}
          </div>
        </Card>
      )}

      <Card title="Activations">
        {license.activations.length === 0 ? (
          <EmptyState>Nothing has activated this licence yet.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Status</Th>
                  <Th>Instance</Th>
                  <Th>Hostname</Th>
                  <Th>Version</Th>
                  <Th>Last seen</Th>
                  <Th>IP</Th>
                  {isAdmin && <Th>Seat</Th>}
                </tr>
              </thead>
              <tbody>
                {license.activations.map((activation) => (
                  <ActivationRow
                    key={activation.id}
                    activation={activation}
                    heartbeatSeconds={heartbeatSeconds}
                    now={now}
                    isAdmin={isAdmin}
                    pending={setActivationRevoked.isPending}
                    onToggle={(revoked) =>
                      setActivationRevoked.mutate({ activationId: activation.id, revoked })
                    }
                  />
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <Card title="Deployment history">
        {deploymentEvents.length === 0 ? (
          <EmptyState>No deployment events yet.</EmptyState>
        ) : (
          <ul className="divide-y divide-hairline">
            {deploymentEvents.map((event) => (
              <li
                key={event.id}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm"
              >
                <span>{EVENT_LABELS[event.type] ?? event.type}</span>
                <span className="text-dim">{new Date(event.at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {isAdmin && (
        <Card
          title="Secrets"
          action={
            <Button onClick={() => rotateSecret.mutate("appKey")} disabled={rotateSecret.isPending}>
              Rotate appKey
            </Button>
          }
        >
          <p className="border-b border-hairline px-4 py-3 text-sm text-muted">
            Rotating issues a new version and keeps the previous one for seven days, so deployments
            can re-wrap their local data key before the old value stops being served.
          </p>
          {license.secrets.length === 0 ? (
            <EmptyState>No secrets issued yet. One is created on first activation.</EmptyState>
          ) : (
            <TableWrap>
              <Table>
                <thead className="border-b border-hairline">
                  <tr>
                    <Th>Name</Th>
                    <Th>Version</Th>
                    <Th>Created</Th>
                    <Th>Superseded</Th>
                    <Th>Stops being served</Th>
                  </tr>
                </thead>
                <tbody>
                  {license.secrets.map((secret) => (
                    <tr
                      key={`${secret.name}-${secret.version}`}
                      className="border-b border-hairline last:border-0"
                    >
                      <Td label="Name" className="tabular">
                        {secret.name}
                      </Td>
                      <Td label="Version" className="tabular">
                        v{secret.version}
                      </Td>
                      <Td label="Created" className="text-muted">
                        {formatDate(secret.createdAt)}
                      </Td>
                      <Td label="Superseded" className="text-muted">
                        {formatDate(secret.supersededAt)}
                      </Td>
                      <Td label="Stops being served" className="text-muted">
                        {formatDate(secret.retiresAt)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>
      )}

      {isAdmin && (
        <Card title="Audit trail">
          {auditLog.length === 0 ? (
            <EmptyState>Nothing recorded for this licence yet.</EmptyState>
          ) : (
            <ul className="divide-y divide-hairline">
              {auditLog.map((entry) => (
                <li key={entry.id} className="flex flex-wrap gap-x-3 px-4 py-2.5 text-sm">
                  <span className="tabular text-dim">{formatDate(entry.createdAt)}</span>
                  <span className="text-ink">{entry.action}</span>
                  <span className="text-dim">{entry.actorType}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {isOwner && (
        <ConfirmDialog
          open={deleting}
          title="Delete this licence"
          confirmPhrase={confirmPhrase}
          confirmLabel="Delete licence"
          pending={remove.isPending}
          error={remove.isError ? (remove.error as Error).message : null}
          onCancel={() => setDeleting(false)}
          onConfirm={(typed) => remove.mutate(typed)}
          description={
            <>
              <p>
                The licence, its domains, its secrets and every activation are erased. This is for
                a licence that should never have existed — if the software is simply meant to stop,
                revoke it instead and keep the history.
              </p>
              <p>
                What this licence <em>did</em> stays in the audit log. A licence issued against a
                paid order cannot be deleted at all.
              </p>
            </>
          }
        />
      )}

      <ConfirmDialog
        open={revoking}
        title="Revoke this licence"
        confirmPhrase={confirmPhrase}
        confirmLabel="Revoke licence"
        pending={revoke.isPending}
        error={revoke.isError ? (revoke.error as Error).message : null}
        onCancel={() => setRevoking(false)}
        onConfirm={(typed) => revoke.mutate(typed)}
        description={
          <>
            <p>
              Every deployment running this licence stops immediately — grace does not apply to an
              answer we gave deliberately. Its seats are released at the same time.
            </p>
            <p>This cannot be undone from here.</p>
          </>
        }
      />
    </div>
  );
}

function ActivationRow({
  activation,
  heartbeatSeconds,
  now,
  isAdmin,
  pending,
  onToggle,
}: {
  activation: Activation;
  heartbeatSeconds: number;
  /** Ticking clock, so the row ages without a reload. See `useNow`. */
  now: number;
  isAdmin: boolean;
  pending: boolean;
  onToggle: (revoked: boolean) => void;
}) {
  const revoked = activation.revokedAt !== null;

  return (
    <tr className={`border-b border-hairline last:border-0 ${revoked ? "opacity-50" : ""}`}>
      <Td label="Status">
        {isReporting(activation.lastSeenAt, heartbeatSeconds, now) ? (
          <Badge tone="live">Reporting</Badge>
        ) : (
          <Badge tone="danger">Not reporting</Badge>
        )}
      </Td>
      <Td label="Instance" className="tabular">
        {activation.instanceId}
      </Td>
      <Td label="Hostname" className="tabular text-muted">
        {activation.hostname}
      </Td>
      <Td label="Version" className="tabular text-muted">
        {activation.appVersion}
      </Td>
      {/* Keyed on the tick so the rounded label is recomputed, not just
          re-rendered with a value React already considers current. */}
      <Td label="Last seen" className="text-muted" title={formatDate(activation.lastSeenAt)}>
        <span key={now}>{formatRelative(activation.lastSeenAt)}</span>
      </Td>
      <Td label="IP" className="tabular text-dim">
        {activation.ipAddress ?? "—"}
      </Td>
      {isAdmin && (
        <Td label="Seat">
          {/* Reversible, so no typed confirmation: the instance re-activates on
              its next call and takes the seat back. */}
          <Button variant="ghost" disabled={pending} onClick={() => onToggle(!revoked)}>
            {revoked ? "Restore seat" : "Release seat"}
          </Button>
        </Td>
      )}
    </tr>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2.5">
      <dt className="text-dim">{label}</dt>
      <dd className="text-ink">{children}</dd>
    </div>
  );
}

function describeDuration(seconds: number): string {
  if (seconds === 0) return "immediately";
  const days = seconds / 86_400;
  if (Number.isInteger(days)) return `${days} day${days === 1 ? "" : "s"}`;
  const hours = Math.round(seconds / 3600);
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}
